import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import Styles from './TopCitiesCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import IFText from 'Components/IFText/IFText'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import IFSkeleton from 'Components/IFSkeleton/IFSkeleton'
import IFTable from 'Components/IFTable/IFTable'
import ReportIcon from '@mui/icons-material/Report'
import { getEnergyUnit } from 'Utils/Calculations'
const TopCitiesCard = ({
  fetchTopCities,
  topCities,
  filter,
  fetchTopCitiesRequestState,
}) => {
  const { t } = useTranslation()
  const tableRef = useRef(null)

  useEffect(() => {
    fetchTopCities(filter)
  }, [filter])

  const [failedRequest, setFailedRequest] = useState(false)
  useEffect(() => {
    if (
      fetchTopCitiesRequestState === RequestState.ERROR_0_NETWORK ||
      fetchTopCitiesRequestState === RequestState.ERROR_400_OCCURRED ||
      fetchTopCitiesRequestState === RequestState.ERROR_401_OCCURRED ||
      fetchTopCitiesRequestState === RequestState.ERROR_403_OCCURRED ||
      fetchTopCitiesRequestState === RequestState.ERROR_409_OCCURRED ||
      fetchTopCitiesRequestState === RequestState.ERROR_UNKNOWN_OCCURRED
    ) {
      setFailedRequest(true)
    }
    if (fetchTopCitiesRequestState === RequestState.SUCCEEDED) {
      setFailedRequest(false)
    }
  }, [fetchTopCitiesRequestState])
  const topCitiesData = Array.isArray(topCities?.data) ? topCities.data : []

  const maxEnergyConsumption = Math.max(
    ...(topCitiesData.map((item) => item.totalEnergyConsumption || 0) ?? [0]),
  )
  const energyUnitConfig = getEnergyUnit(maxEnergyConsumption)

  const TopCitiesTableColumns = useMemo(
    () => [
      {
        accessorKey: 'city',
        enableEditing: false,
        size: 30,

        muiTableHeadCellProps: {
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
          },
        },
        header:
          fetchTopCitiesRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div
              style={{
                marginLeft: '1rem',
              }}
            >
              {t('Analytics.City')}
            </div>
          ),
        Cell: ({ row }) => {
          return row.index < topCities?.data?.length ? (
            <div
              style={{
                marginLeft: '1rem',
              }}
            >
              <IFText>{row.original.city}</IFText>
            </div>
          ) : failedRequest ? (
            <div>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'sitesCount',
        enableEditing: false,
        size: 30,
        muiTableHeadCellProps: {
          align: 'center',
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
            padding: '0 !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 !important',
          },
        },
        header:
          fetchTopCitiesRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div>{t('Analytics.SitesCount')}</div>
          ),
        Cell: ({ row }) => {
          return row.index < topCities?.data?.length ? (
            <div className={Styles.Header}>
              <IFText>{row.original.stationCount}</IFText>
            </div>
          ) : failedRequest ? (
            <div>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'transactionCount',
        enableEditing: false,
        size: 50,
        muiTableHeadCellProps: {
          align: 'center',
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
            padding: '0 !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 !important',
          },
        },
        header:
          fetchTopCitiesRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div>{t('Analytics.NumberOfTransactions')}</div>
          ),
        Cell: ({ row }) => {
          return row.index < topCities?.data?.length ? (
            <div className={Styles.Header}>
              <IFText>{row.original.transactionCount}</IFText>
            </div>
          ) : failedRequest ? (
            <div>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'energyConsumption',
        enableEditing: false,
        size: 70,
        muiTableHeadCellProps: {
          align: 'center',
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
            padding: '0 !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 !important',
          },
        },
        header:
          fetchTopCitiesRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div>
              {' '}
              {t('Analytics.EnergyConsumption')} ({energyUnitConfig.unit})
            </div>
          ),
        Cell: ({ row }) => {
          if (row.index < topCities?.data?.length) {
            const rawValue = row.original.totalEnergyConsumption ?? 0

            const formattedValue = (
              rawValue / energyUnitConfig.divisor
            ).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })

            return (
              <div className={Styles.Header}>
                <IFText>{formattedValue}</IFText>
              </div>
            )
          }

          if (failedRequest) {
            return (
              <div>
                <ReportIcon
                  width={28}
                  height={28}
                  style={{ color: Colors.red }}
                />
                <IFText>{t('IFTable.TryAgainText')}</IFText>
              </div>
            )
          }

          return (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
    ],
    [topCities, fetchTopCities, fetchTopCitiesRequestState, failedRequest],
  )
  const loadMoreData = () => {
    if (fetchTopCitiesRequestState === RequestState.LOADING) return
    fetchTopCities(filter)
  }
  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )

  return (
    <IFTable
      columns={TopCitiesTableColumns}
      ref={tableRef}
      tableMaxHeight={486}
      filters={filter}
      tableRequestState={fetchTopCitiesRequestState}
      fetchMoreOnBottomReached={fetchMoreOnBottomReached}
      data={topCities.data}
      showSkeleton={
        fetchTopCitiesRequestState === RequestState.LOADING ||
        (fetchTopCitiesRequestState !== RequestState.SUCCEEDED &&
          topCities?.data.length === 0)
      }
    />
  )
}

const mapStateToProps = (state) => ({
  topCities: AnalyticsSelectors.getTopCities(state),
  fetchTopCitiesRequestState:
    AnalyticsSelectors.getFetchTopCitiesRequestState(state),
})

const mapDispatchToProps = (dispatch) => ({
  fetchTopCities: (filter) => dispatch(AnalyticsActions.fetchTopCities(filter)),
})

TopCitiesCard.propTypes = {
  filter: PropTypes.object,
  topCities: PropTypes.object,
  fetchTopCities: PropTypes.func,
  fetchTopCitiesRequestState: PropTypes.number,
}

export default connect(mapStateToProps, mapDispatchToProps)(TopCitiesCard)
